import type {
  Fact,
  FactCatalog,
  FactId,
  LessonPhase,
  ResponseMode,
  StrategyDefinition,
  StrategyId,
  SupportLevel,
} from "../model.ts";
import { factExpression, strategyGuidance, strategyWorkedExample } from "./strategy-scaffolding.ts";
import { answerExposureFactIds } from "./answer-exposure.ts";

export type LessonVariant = "FULL" | "REFRESH" | "CHECK";

export interface LessonQuestion {
  readonly id: string;
  readonly prompt: string;
  readonly expectedAnswer: number;
}

export interface LessonActivity {
  readonly answerExposureFactIds?: readonly FactId[];
  readonly id: string;
  readonly phase: LessonPhase;
  readonly title: string;
  readonly copy: readonly string[];
  readonly factId: FactId | null;
  readonly expression: string | null;
  readonly questions: readonly LessonQuestion[];
  readonly responseMode: ResponseMode | null;
  readonly supportLevel: SupportLevel | null;
  readonly retrievalEvidence: false;
  readonly strategyEvidence: boolean;
}

export interface StrategyLessonInstance {
  readonly id: string;
  readonly strategyId: StrategyId;
  readonly title: string;
  readonly variant: LessonVariant;
  readonly activities: readonly LessonActivity[];
}

function hash(seed: string): number {
  let value = 2166136261;
  for (const character of seed) {
    value ^= character.charCodeAt(0);
    value = Math.imul(value, 16777619);
  }
  return value >>> 0;
}

function questionsFor(strategyId: StrategyId, fact: Fact, phase: LessonPhase): LessonQuestion[] {
  const finalAnswer = { id: "answer", prompt: `${factExpression(fact)} =`, expectedAnswer: fact.answer };
  if (phase === "INDEPENDENT_CHECK") return [finalAnswer];
  if (strategyId === "VIA_10_ADD" && fact.operation === "ADD") {
    const toTen = 10 - fact.left;
    return [
      { id: "to-ten", prompt: `Hoeveel heeft ${fact.left} nodig om 10 te worden?`, expectedAnswer: toTen },
      { id: "remainder", prompt: `Hoeveel blijven er van de ${fact.right} over?`, expectedAnswer: fact.right - toTen },
      finalAnswer,
    ];
  }
  if (strategyId === "BACK_VIA_10" && fact.operation === "SUBTRACT") {
    const toTen = fact.left - 10;
    return [
      { id: "to-ten", prompt: `Hoeveel haal je eerst van ${fact.left} af om op 10 te komen?`, expectedAnswer: toTen },
      { id: "remainder", prompt: `Hoeveel moeten er daarna nog af?`, expectedAnswer: fact.right - toTen },
      finalAnswer,
    ];
  }
  return [finalAnswer];
}

function candidates(
  strategy: StrategyDefinition,
  catalog: FactCatalog,
): Fact[] {
  return catalog.facts.filter((fact) =>
    fact.strategyTags.includes(strategy.id)
    && strategy.supportedOperations.includes(fact.operation)
    && (strategy.id !== "MAKE_10" || (fact.operation === "ADD" ? fact.answer === 10 : fact.left === 10))
  );
}

function chooseFact(
  options: readonly Fact[],
  seed: string,
  excluded: ReadonlySet<FactId>,
): Fact | null {
  const available = options.filter((fact) => !excluded.has(fact.id));
  const pool = available;
  if (pool.length === 0) return null;
  return pool[hash(seed) % pool.length];
}

function activity(
  strategy: StrategyDefinition,
  phase: LessonPhase,
  fact: Fact | null,
  ordinal: number,
): LessonActivity {
  const definition = strategy.lesson!.steps.find((step) => step.phase === phase)!;
  const asksQuestions = phase === "GUIDED_APPLICATION"
    || phase === "PARTIAL_APPLICATION"
    || phase === "INDEPENDENT_CHECK";
  return {
    id: `${strategy.lesson!.id}.${phase}.${ordinal}`,
    phase,
    title: definition.title,
    copy: phase === "INDEPENDENT_CHECK" ? ["Probeer deze nieuwe som zelf."]
      : fact && phase === "WORKED_EXAMPLE" ? strategyWorkedExample(strategy.id, fact)
      : fact ? strategyGuidance(strategy.id, fact) : definition.copy,
    factId: fact?.id ?? null,
    expression: fact ? factExpression(fact) : null,
    questions: fact && asksQuestions ? questionsFor(strategy.id, fact, phase) : [],
    responseMode: asksQuestions ? "NUMERIC_ENTRY" : definition.responseMode,
    supportLevel: definition.supportLevel,
    retrievalEvidence: false,
    strategyEvidence: definition.strategyEvidence,
  };
}

export function instantiateStrategyLesson(
  strategyId: StrategyId,
  catalog: FactCatalog,
  strategies: readonly StrategyDefinition[],
  seed: string,
  variant: LessonVariant = "FULL",
): StrategyLessonInstance {
  const strategy = strategies.find((candidate) => candidate.id === strategyId);
  if (!strategy?.lesson) throw new Error(`Strategie ${strategyId} heeft geen Strategy Lesson.`);
  const facts = candidates(strategy, catalog);
  if (facts.length === 0) throw new Error(`Strategie ${strategyId} heeft geen veilige lesvoorbeelden.`);

  const phases: readonly LessonPhase[] = variant === "FULL"
    ? ["EXPLANATION", "WORKED_EXAMPLE", "GUIDED_APPLICATION", "PARTIAL_APPLICATION", "INDEPENDENT_CHECK"]
    : variant === "CHECK" ? ["INDEPENDENT_CHECK"]
    : ["WORKED_EXAMPLE", "GUIDED_APPLICATION", "INDEPENDENT_CHECK"];
  const excluded = new Set<FactId>();
  const activities = phases.map((phase, index) => {
    let fact: Fact | null = null;
    if (phase !== "EXPLANATION") {
      const workedExamples = strategy.lesson!.exampleFactIds
        .map((factId) => catalog.factById.get(factId))
        .filter((item): item is Fact => item !== undefined && facts.some((fact) => fact.id === item.id));
      let pool = phase === "WORKED_EXAMPLE" && workedExamples.length > 0
        ? workedExamples
        : facts;
      if (phase === "INDEPENDENT_CHECK") {
        const exposedFamilies = new Set([...excluded].map((id) => catalog.factById.get(id)?.familyId).filter(Boolean));
        pool = pool.filter((candidate) => !candidate.familyId || !exposedFamilies.has(candidate.familyId));
      }
      fact = chooseFact(pool, `${seed}:${phase}`, excluded);
      if (!fact) throw new Error(`Strategie ${strategyId} heeft onvoldoende verschillende veilige lesvoorbeelden.`);
      if (fact) excluded.add(fact.id);
      if (fact && phase !== "INDEPENDENT_CHECK") {
        for (const id of answerExposureFactIds(fact, strategy.id, catalog)) excluded.add(id);
      }
    }
    return { ...activity(strategy, phase, fact, index + 1),
      answerExposureFactIds: fact ? answerExposureFactIds(fact, strategy.id, catalog) : [] };
  });
  return {
    id: `${strategy.lesson.id}.${variant}.${hash(seed).toString(16)}`,
    strategyId,
    title: strategy.lesson.title,
    variant,
    activities,
  };
}

export function lessonActivityIsCorrect(
  activity: LessonActivity,
  answers: Readonly<Record<string, number>>,
): boolean {
  return activity.questions.length > 0
    && activity.questions.every((question) => answers[question.id] === question.expectedAnswer);
}
